import {
  TodayAssessmentFace,
  type TodayAssessmentItem,
  TodayAssessmentSlideState,
  type TodayAssessmentSlide,
  TodayAssessmentStatus,
  type TodayAssessmentView,
} from '@/ts/types/component/today-page';

/**
 * Status first, never "does it carry a date". An open assessment CAN carry one, with a moment in the
 * past: when everything has been answered the backend keeps whichever comes back soonest, and once
 * that moment passes it returns it open, still dated with the moment it reopened (its
 * `nearestToReopen`, and the test "one whose cooldown already expired comes back open"). Reading the
 * date first would show a countdown on the one card the user can actually answer.
 *
 * The duration is dropped from every slide that is not counting down, so a template cannot render
 * one by reaching for a field that should not be there.
 */
function toSlide(item: TodayAssessmentItem): TodayAssessmentSlide {
  const {id, title, imageUrl, artUrl, backgroundColor} = item;

  if (item.status === TodayAssessmentStatus.available) {
    return {id, title, imageUrl, artUrl, backgroundColor, state: TodayAssessmentSlideState.open, unlockInMillis: null};
  }
  // A locked one whose moment has already gone by keeps its card and loses its countdown: an
  // elapsed one says nothing, and the payload is a snapshot the page may be holding for a while.
  if (item.unlockInMillis === null || item.unlockInMillis <= 0) {
    return {id, title, imageUrl, artUrl, backgroundColor, state: TodayAssessmentSlideState.queued, unlockInMillis: null};
  }
  return {
    id,
    title,
    imageUrl,
    artUrl,
    backgroundColor,
    state: TodayAssessmentSlideState.cooldown,
    unlockInMillis: item.unlockInMillis,
  };
}

/**
 * Whether anything is open is asked of the whole list, not of its head. The contract puts the open
 * one first and every payload that keeps its word gives the same answer either way — but one that
 * does not would otherwise have the tile contradict itself out loud: "unlocks after your first
 * steps" across the top, and a working button one swipe away. The endpoint is unmerged and its
 * contract unratified, which is exactly when a defence like this is worth its line.
 *
 * The rest follows the head, since that is the card the user lands on. Order is never rearranged
 * here: the backend decides what comes first.
 *
 * What the backend actually sends per situation, verified against its service and tests
 * (TodayAssessmentService on the IPD-2968 branch) rather than assumed from the spec:
 *
 * - Everything answered: EXACTLY one item, by construction — the service keeps the nearest to
 *   reopen and discards the rest (`AssessmentTile(listOf(nearest))`). The countdown face is
 *   therefore drawn without company today; it does not depend on that holding.
 * - Onboarding open (enrolled without answering it): the onboarding heads the list and the rest
 *   of the pool arrives LOCKED behind it — its test pins `items.last()` as locked. The prototype
 *   draws this state as a single card without a carousel; spec and backend disagree with it, and
 *   design owns the call. If it lands on the single card, the trim belongs in the mapper, not here.
 * - First use (onboarding CTA held by the Upcoming tile): the whole queue arrives locked, and the
 *   backend has already removed the onboarding questionnaire from it — the design rule that the
 *   generic copy must not name it is upheld upstream, not by this file.
 */
function faceOf(slides: readonly TodayAssessmentSlide[]): TodayAssessmentFace {
  if (slides.some((slide) => slide.state === TodayAssessmentSlideState.open)) {
    return TodayAssessmentFace.available;
  }
  const [head] = slides;
  if (head?.state === TodayAssessmentSlideState.queued) {
    return TodayAssessmentFace.locked;
  }
  // A head counting down is the countdown state whatever follows it. How many cards are behind it
  // does not change the drawing: the design's state 4 is "the next most important assessment plus
  // its Unlocks in XX days" (IPD-2905), and the prototype draws it as the card alone, with the
  // slider chrome simply absent when there is nothing to page to.
  return TodayAssessmentFace.countdown;
}

/**
 * Translates the assessment payload into the states the design draws (IPD-2905, IPD-3052).
 *
 * The payload does not name any of them: it states a status per item plus, for a locked one, how
 * long until it reopens. The five states of the design come from the shape of the list, and this is
 * the only place that knows the correspondence — the tile renders a resolved face.
 *
 *   design state                    | shape of the payload
 *   --------------------------------|-----------------------------------------------------
 *   Locked (first use)              | nothing open, and the head has nothing to count down
 *   Onboarding CTA / Assessment     | anything in the list is open
 *   Locked with countdown           | nothing open, and the head says when it reopens
 *   All caught up                   | no payload produces it (see below)
 *   Hidden                          | no tile in the payload (see the return contract below)
 *
 * "All caught up" is a state of the design that never reaches a drawing here, and deliberately so.
 * Its condition is "no next assessment exists" (IPD-2905, state 5), and the backend answers that
 * situation by sending no tile at all — which is also how the prototype draws it, with the row
 * closing over the gap. Everything-answered-but-one-comes-back is not that state: it is state 4,
 * the card with its "Unlocks in XX days" and no line above it. The frontend used to split state 4
 * on the number of cards and lead the single-card case with an "all caught up" line the prototype
 * does not show; it no longer does, and the copy string went with it.
 *
 * Two of the design's states collapse on purpose. "Onboarding CTA" and "Assessment available" are
 * the same drawing — one open card whose title is the questionnaire's own name — and which
 * questionnaire is at the head is the backend's business: it puts the onboarding one there while it
 * is unanswered, and leaves nothing open at all while its CTA sits in the Upcoming tile
 * (`TodayService`, `ctaInUpcoming`). So the frontend needs no way to recognise it, which is just as
 * well: the payload has none.
 *
 * It takes no clock. Every duration arrives already counted, so the same input always gives the same
 * output, and nothing here can disagree with the backend's own three-day threshold.
 *
 * @returns the resolved tile, or null when there is none to draw. The payload leaves the tile out
 *   for three different reasons — nothing recommended, nothing that ever reopens, nothing due for
 *   more than three days — and all three look identical here: no tile, and the journal expands into
 *   the row on its own. An empty list is not one of them, since the service never sends one, but it
 *   resolves the same way rather than leaving a caller holding a tile with no cards.
 */
export function resolveTodayAssessments(
  items: readonly TodayAssessmentItem[] | null | undefined,
): TodayAssessmentView | null {
  const slides = (items ?? []).map(toSlide);
  const [head] = slides;
  if (!head) {
    return null;
  }

  return {face: faceOf(slides), slides};
}
